import React, { useState } from 'react';
import {
  Sparkles,
  Info,
  CheckCircle,
  AlertTriangle,
  ShieldAlert,
  X,
  ChevronRight,
  ChevronLeft,
  ExternalLink,
  Megaphone
} from 'lucide-react';
import { AnnouncementBanner, BannerTone } from '../types.ts';
import { DynamicIcon } from './DynamicIcon.tsx';

interface BannerBarProps {
  announcements: AnnouncementBanner[];
  onDismiss: (id: string) => void;
  onOpenModal: (announcement: AnnouncementBanner) => void;
  onNavigateTab?: (tabId: string) => void;
}

const TONE_CONFIG: Record<
  BannerTone,
  {
    wrapperClass: string;
    badgeClass: string;
    btnClass: string;
    defaultIcon: React.ComponentType<{ className?: string }>;
    defaultTag: string;
  }
> = {
  feature: {
    wrapperClass:
      'bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-950 text-white border-b border-indigo-500/30 shadow-md',
    badgeClass:
      'bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 ring-1 ring-indigo-400/20',
    btnClass:
      'bg-white text-indigo-950 hover:bg-indigo-50 active:scale-[0.98] shadow-sm',
    defaultIcon: Sparkles,
    defaultTag: 'WHAT’S NEW',
  },
  info: {
    wrapperClass:
      'bg-blue-600 text-white border-b border-blue-700/50 shadow-sm',
    badgeClass:
      'bg-blue-800/40 text-blue-100 border border-blue-400/30',
    btnClass:
      'bg-white text-blue-900 hover:bg-blue-50 active:scale-[0.98] shadow-sm',
    defaultIcon: Info,
    defaultTag: 'INFO',
  },
  success: {
    wrapperClass:
      'bg-gradient-to-r from-emerald-700 to-teal-800 text-white border-b border-emerald-600/50 shadow-sm',
    badgeClass:
      'bg-emerald-900/40 text-emerald-100 border border-emerald-400/30',
    btnClass:
      'bg-white text-emerald-900 hover:bg-emerald-50 active:scale-[0.98] shadow-sm',
    defaultIcon: CheckCircle,
    defaultTag: 'SUCCESS',
  },
  warning: {
    wrapperClass:
      'bg-gradient-to-r from-amber-600 to-amber-700 text-amber-950 dark:text-white border-b border-amber-500/50 shadow-sm',
    badgeClass:
      'bg-amber-900/30 text-amber-100 border border-amber-400/40',
    btnClass:
      'bg-amber-950 text-white hover:bg-amber-900 active:scale-[0.98] shadow-sm',
    defaultIcon: AlertTriangle,
    defaultTag: 'NOTICE',
  },
  alert: {
    wrapperClass:
      'bg-gradient-to-r from-red-600 to-rose-700 text-white border-b border-red-500/50 shadow-sm',
    badgeClass:
      'bg-red-950/40 text-red-100 border border-red-400/40',
    btnClass:
      'bg-white text-red-900 hover:bg-red-50 active:scale-[0.98] shadow-sm',
    defaultIcon: ShieldAlert,
    defaultTag: 'URGENT',
  },
};

export default function BannerBar({
  announcements,
  onDismiss,
  onOpenModal,
  onNavigateTab,
}: BannerBarProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!announcements || announcements.length === 0) {
    return null;
  }

  // Safe bounds check
  const activeBanner = announcements[currentIndex] || announcements[0];
  if (!activeBanner) return null;

  const tone = activeBanner.tone || 'feature';
  const config = TONE_CONFIG[tone] || TONE_CONFIG.feature;
  const FallbackIcon = config.defaultIcon;

  const hasMultiple = announcements.length > 1;

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  };

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
  };

  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (activeBanner.ctaAction === 'open_modal' || (!activeBanner.ctaAction && !activeBanner.ctaUrl)) {
      onOpenModal(activeBanner);
    } else if (activeBanner.ctaUrl) {
      if (activeBanner.ctaUrl.startsWith('http://') || activeBanner.ctaUrl.startsWith('https://')) {
        window.open(activeBanner.ctaUrl, '_blank', 'noopener,noreferrer');
      } else if (onNavigateTab) {
        onNavigateTab(activeBanner.ctaUrl);
      }
    } else {
      onOpenModal(activeBanner);
    }
  };

  return (
    <div
      role="region"
      aria-label="Announcement Banner"
      className={`relative z-30 transition-all duration-300 px-3 py-2 sm:px-4 sm:py-2.5 ${config.wrapperClass}`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3 text-xs md:text-sm font-medium">
        
        {/* Left icon & tone badge */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="shrink-0 flex items-center justify-center w-6 h-6 rounded-md bg-white/15 backdrop-blur-xs">
            {activeBanner.featureItems && activeBanner.featureItems[0]?.iconName ? (
              <DynamicIcon
                name={activeBanner.featureItems[0].iconName}
                className="w-3.5 h-3.5"
                fallback={FallbackIcon}
              />
            ) : (
              <FallbackIcon className="w-3.5 h-3.5" />
            )}
          </div>

          <span className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded text-[10px] font-black tracking-wider uppercase font-mono shrink-0 ${config.badgeClass}`}>
            {config.defaultTag}
          </span>

          {/* Title & Message */}
          <div className="flex items-center gap-2 truncate">
            <span className="font-extrabold tracking-tight shrink-0">
              {activeBanner.title}
            </span>
            <span className="hidden md:inline text-white/80 font-normal truncate">
              — {activeBanner.message}
            </span>
          </div>
        </div>

        {/* Right Action buttons, pagination, & Dismiss */}
        <div className="flex items-center gap-2 shrink-0">
          {/* CTA Action Button */}
          {(activeBanner.ctaLabel || activeBanner.featureItems?.length) && (
            <button
              onClick={handleCtaClick}
              className={`px-3 py-1 rounded-md text-xs font-black transition cursor-pointer flex items-center gap-1.5 shrink-0 ${config.btnClass}`}
            >
              <span>{activeBanner.ctaLabel || "See What's New"}</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Multi-banner pagination if > 1 */}
          {hasMultiple && (
            <div className="flex items-center gap-1 bg-black/20 rounded-md px-1 py-0.5 ml-1 text-white/90">
              <button
                onClick={handlePrev}
                title="Previous Announcement"
                className="p-0.5 hover:bg-white/20 rounded cursor-pointer transition"
              >
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="text-[10px] font-mono font-bold px-1">
                {currentIndex + 1}/{announcements.length}
              </span>
              <button
                onClick={handleNext}
                title="Next Announcement"
                className="p-0.5 hover:bg-white/20 rounded cursor-pointer transition"
              >
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          )}

          {/* Dismiss button */}
          {activeBanner.dismissible !== false && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(activeBanner.id);
              }}
              title="Dismiss announcement"
              className="p-1 rounded-md text-white/70 hover:text-white hover:bg-white/20 transition cursor-pointer ml-1"
              aria-label="Dismiss banner"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
